import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReportShell, { useReportDates } from '../../components/reports/ReportShell';
import {
  BusyHeatmap,
  ChartCard,
  KpiCard,
  SimpleBar,
  SimpleLine,
  SimplePie,
  dictToPie,
  formatDurationSeconds,
} from '../../components/reports/ReportCharts';
import { reportsApi } from '../../services/reportsApi';
import { downloadCsv } from '../../components/reports/reportExport';
import { useKeepGrantedTab } from '../../brand/MenuPolicyContext';
import { POOL_REPORT_TAB_IDS, hiddenTabProps } from '../../brand/menuCatalog';

type Props = { poolId: number; poolName?: string };

const emptyOverview = (poolId: number, poolName?: string) => ({
  pool: { id: poolId, name: poolName || '', status: '-', capacity: null, current_occupancy: null },
  inventory: { cctv: 0, members: 0, wearable_devices: 0 },
  events: { total: 0, by_day: [], by_type: {} },
  wearable_sessions: 0,
  alerts_dispatched: 0,
  water_quality: null,
  safety_status: null,
});

const PoolReportsPanel: React.FC<Props> = ({ poolId, poolName }) => {
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useReportDates(30);
  const [tab, setTab] = useState(0);
  const reportTabOn = useKeepGrantedTab(tab, POOL_REPORT_TAB_IDS, setTab);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<any>(null);
  const [events, setEvents] = useState<any>(null);
  const [wear, setWear] = useState<any>(null);
  const [members, setMembers] = useState<any>(null);
  const [busy, setBusy] = useState<any>(null);
  const [alerts, setAlerts] = useState<any>(null);
  const [emptyHint, setEmptyHint] = useState(false);

  const load = useCallback(async () => {
    if (!poolId) return;
    setLoading(true);
    setEmptyHint(false);
    try {
      await reportsApi.ping();
    } catch (e) {
      // ping 실패해도 개별 API 시도 — 신규·빈 시설은 빈 KPI만 표시 (붉은 오류 없음)
      console.warn('[pool-reports] ping skipped', e);
    }
    const period = { date_from: dateFrom, date_to: dateTo };
    const settled = await Promise.allSettled([
      reportsApi.poolOverview(poolId, period),
      reportsApi.poolEvents(poolId, period),
      reportsApi.poolWearables(poolId, period),
      reportsApi.poolMembersActivity(poolId, period),
      reportsApi.poolBusyHours(poolId, period),
      reportsApi.poolAlerts(poolId, period),
    ]);
    settled.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.warn(`[pool-reports] slot ${i} skipped`, r.reason);
      }
    });
    const ov =
      settled[0].status === 'fulfilled' ? settled[0].value : emptyOverview(poolId, poolName);
    setOverview(ov);
    setEvents(settled[1].status === 'fulfilled' ? settled[1].value : { by_hour: [], by_status: {}, mttr_seconds_avg: null });
    setWear(
      settled[2].status === 'fulfilled'
        ? settled[2].value
        : {
            devices_total: 0,
            currently_assigned: 0,
            sessions_count: 0,
            avg_session_minutes: '-',
            by_status: {},
            battery_bins: {},
            sessions_by_day: [],
          },
    );
    setMembers(
      settled[3].status === 'fulfilled'
        ? settled[3].value
        : { total_members: 0, members: [] },
    );
    setBusy(settled[4].status === 'fulfilled' ? settled[4].value : null);
    setAlerts(settled[5].status === 'fulfilled' ? settled[5].value : { pool_total: 0, by_channel: [] });
    const allFailed = settled.every((r) => r.status === 'rejected');
    const noData =
      allFailed ||
      ((Number(ov?.events?.total) || 0) === 0 &&
        (Number(ov?.wearable_sessions) || 0) === 0 &&
        (Number(ov?.alerts_dispatched) || 0) === 0);
    setEmptyHint(noData);
    setLoading(false);
  }, [poolId, poolName, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCsv = () => {
    if (tab === 3 && members?.members) {
      downloadCsv(`pool_${poolId}_members.csv`, [
        ['name', 'sessions', 'minutes', 'events'],
        ...members.members.map((r: any) => [r.name, r.sessions, r.session_minutes, r.events]),
      ]);
    } else if (overview) {
      downloadCsv(`pool_${poolId}_overview.csv`, [
        ['metric', 'value'],
        ['events', overview.events?.total],
        ['sessions', overview.wearable_sessions],
        ['alerts', overview.alerts_dispatched],
      ]);
    }
  };

  return (
    <ReportShell
      title={`시설 리포트${poolName ? ` — ${poolName}` : ''}`}
      printRootId={`pool-report-print-${poolId}`}
      loading={loading}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateFrom={setDateFrom}
      onDateTo={setDateTo}
      onRefresh={load}
      onCsv={onCsv}
    >
      {emptyHint && (
        <Alert severity="info" sx={{ mb: 2 }}>
          이 기간에 집계할 자료 없음 (신규 시설·미사용 기간이면 정상).
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} className="no-print" variant="scrollable">
        <Tab label="운영 요약" {...hiddenTabProps(reportTabOn(0))} />
        <Tab label="이벤트" {...hiddenTabProps(reportTabOn(1))} />
        <Tab label="웨어러블" {...hiddenTabProps(reportTabOn(2))} />
        <Tab label="회원 활동" {...hiddenTabProps(reportTabOn(3))} />
        <Tab label="혼잡·알림" {...hiddenTabProps(reportTabOn(4))} />
        <Tab label="수질·안전" {...hiddenTabProps(reportTabOn(5))} />
      </Tabs>

      {tab === 0 && overview && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <KpiCard label="CCTV" value={overview.inventory?.cctv ?? 0} />
          </Grid>
          <Grid item xs={6} md={2}>
            <KpiCard label="회원" value={overview.inventory?.members ?? 0} />
          </Grid>
          <Grid item xs={6} md={2}>
            <KpiCard label="웨어러블" value={overview.inventory?.wearable_devices ?? 0} />
          </Grid>
          <Grid item xs={6} md={2}>
            <KpiCard label="이벤트" value={overview.events?.total ?? 0} />
          </Grid>
          <Grid item xs={6} md={2}>
            <KpiCard label="착용 세션" value={overview.wearable_sessions ?? 0} />
          </Grid>
          <Grid item xs={6} md={2}>
            <KpiCard label="알림 발송" value={overview.alerts_dispatched ?? 0} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="일별 이벤트">
              <SimpleLine data={overview.events?.by_day || []} xKey="date" yKey="count" />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="이벤트 유형">
              <SimplePie data={dictToPie(overview.events?.by_type || {})} />
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              현재 점유 {overview.pool?.current_occupancy ?? '-'} / 수용 {overview.pool?.capacity ?? '-'} · 상태{' '}
              {overview.pool?.status}
            </Typography>
          </Grid>
        </Grid>
      )}

      {tab === 1 && events && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartCard title="시간대별">
              <SimpleBar data={events.by_hour || []} xKey="hour" yKey="count" />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="상태">
              <SimplePie data={dictToPie(events.by_status || {})} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <KpiCard
              label="평균 해결시간"
              value={formatDurationSeconds(events.mttr_seconds_avg)}
              hint={
                events.mttr_sample_count
                  ? `표본 ${events.mttr_sample_count}건`
                  : '해결된 이벤트 없음'
              }
            />
          </Grid>
        </Grid>
      )}

      {tab === 2 && wear && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <KpiCard label="장치 수" value={wear.devices_total ?? 0} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard label="현재 착용" value={wear.currently_assigned ?? 0} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard label="기간 세션" value={wear.sessions_count ?? 0} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard label="평균 세션(분)" value={wear.avg_session_minutes ?? '-'} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="상태별 장치">
              <SimplePie data={dictToPie(wear.by_status || {})} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="배터리 구간">
              <SimplePie data={dictToPie(wear.battery_bins || {})} />
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <ChartCard title="일별 세션">
              <SimpleBar data={wear.sessions_by_day || []} xKey="date" yKey="count" color="#00838f" />
            </ChartCard>
          </Grid>
        </Grid>
      )}

      {tab === 3 && members && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            활성 회원 {members.total_members ?? 0}명 (상위 활동 표시)
          </Typography>
          {(members.members || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              회원 활동 없음
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>회원</TableCell>
                  <TableCell align="right">세션</TableCell>
                  <TableCell align="right">체류(분)</TableCell>
                  <TableCell align="right">이벤트</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(members.members || []).map((r: any) => (
                  <TableRow key={r.member_id}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.sessions}</TableCell>
                    <TableCell align="right">{r.session_minutes}</TableCell>
                    <TableCell align="right">{r.events}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {tab === 4 && (
        <Grid container spacing={2}>
          {busy && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                혼잡 히트맵
              </Typography>
              <BusyHeatmap labels={busy.weekday_labels || []} grid={busy.grid || []} />
            </Grid>
          )}
          {alerts && (
            <Grid item xs={12} md={6}>
              <KpiCard label="이 풀 알림 건수" value={alerts.pool_total ?? 0} />
            </Grid>
          )}
          {alerts && (
            <Grid item xs={12} md={6}>
              <ChartCard title="채널별(전체 기준 참고)">
                <SimpleBar
                  data={(alerts.by_channel || []).map((r: any) => ({
                    name: r.channel,
                    count: (r.success || 0) + (r.failed || 0),
                  }))}
                  xKey="name"
                  yKey="count"
                />
              </ChartCard>
            </Grid>
          )}
          {!busy && !alerts ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                혼잡·알림 자료 없음
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      )}

      {tab === 5 && overview && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={600}>
              수질 (최신)
            </Typography>
            {overview.water_quality ? (
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>수온</TableCell>
                    <TableCell>{overview.water_quality.temperature}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>pH</TableCell>
                    <TableCell>{overview.water_quality.ph_level}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>염소</TableCell>
                    <TableCell>{overview.water_quality.chlorine_level}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>탁도</TableCell>
                    <TableCell>{overview.water_quality.turbidity}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">수질 데이터 없음</Alert>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={600}>
              안전 상태 (최신)
            </Typography>
            {overview.safety_status ? (
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>인명구조 인원</TableCell>
                    <TableCell>{overview.safety_status.lifeguard_count}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>최근 점검</TableCell>
                    <TableCell>{overview.safety_status.last_inspection_date || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>다음 점검</TableCell>
                    <TableCell>{overview.safety_status.next_inspection_date || '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">안전 상태 데이터 없음</Alert>
            )}
          </Grid>
        </Grid>
      )}
    </ReportShell>
  );
};

export default PoolReportsPanel;
